import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button as MuiButton,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  configurationsGetConfigurationOptions,
  configurationsListMarketingPresentationTemplateCatalogOptions,
  configurationsSyncBrokerAssignmentsMutation,
  configurationsUpdateConfigurationMutation,
  useApi,
  useHeyApi,
} from "@platform/api";
import { Alert, Button, Typography } from "@platform/design-system";
import { useNotification } from "@platform/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { Loader } from "../../components";
import { ROUTE_NAMES } from "../../constants";
import { extractErrorMessage } from "../../utils";
import { BrokerPartner } from "../Brokers/types";

import { ColorField } from "./ColorField";
import { fieldCatalog } from "./fieldCatalog";
import { GRID_STYLE_FIELDS, THEME_COLOR_FIELDS } from "./styleCatalog";

import type {
  ConfigurationData,
  GridStyle,
  PresentationStyle,
  ThemeColors,
} from "@platform/api";

const STATUS_COLORS: Record<
  string,
  "default" | "success" | "warning" | "error"
> = {
  draft: "default",
  published: "success",
  unpublished: "warning",
};

// Financial Summary and Client Profile are not coverages, so they get their own
// sections rather than a tab among the lines of coverage.
const NON_COVERAGE_GROUPS = new Set(["Financial", "Client"]);

const coverageSheets = fieldCatalog.filter(
  (sheet) =>
    sheet.fields.length > 0 && !NON_COVERAGE_GROUPS.has(sheet.coverageGroup),
);

const coverageGroups = [
  ...new Set(coverageSheets.map((sheet) => sheet.coverageGroup)),
];

const financialSummarySheet = fieldCatalog.find(
  (sheet) => sheet.sheetKey === "financial_summary_page",
);

const clientProfileSheet = fieldCatalog.find(
  (sheet) => sheet.sheetKey === "client_profile",
);

const emptyConfiguration = (): ConfigurationData => ({
  quotes: {},
  financial: { dollar_difference_from_current: false },
});

// Drop empty color slots so the backend's per-field defaults apply; collapse an
// all-empty grid/theme/style to undefined so no stray objects are persisted.
const pruneColors = <T extends Record<string, string | undefined>>(
  colors: T | undefined,
): T | undefined => {
  if (!colors) return undefined;
  const kept = Object.entries(colors).filter(([, value]) => value?.trim());
  return kept.length > 0 ? (Object.fromEntries(kept) as T) : undefined;
};

const prunePresentationStyle = (
  style: PresentationStyle | undefined,
): PresentationStyle | undefined => {
  const grid = pruneColors(style?.grid);
  const theme = pruneColors(style?.theme);
  if (!grid && !theme) return undefined;
  return { ...(grid && { grid }), ...(theme && { theme }) };
};

export const ConfigurationEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { centroPortalClient } = useHeyApi();
  const axiosInstance = useApi();

  const [activeTab, setActiveTab] = useState(0);
  const [activeCoverageGroup, setActiveCoverageGroup] = useState(
    coverageGroups[0] ?? "",
  );
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [configuration, setConfiguration] =
    useState<ConfigurationData>(emptyConfiguration);
  const [pendingAssignments, setPendingAssignments] = useState<BrokerPartner[]>(
    [],
  );
  const [brokerInput, setBrokerInput] = useState("");
  const [isSavingAssignments, setIsSavingAssignments] = useState(false);
  const { showSuccess, showError } = useNotification();

  const {
    data: config,
    isLoading,
    error,
  } = useQuery(
    configurationsGetConfigurationOptions({
      path: { id: id! },
      client: centroPortalClient,
    }),
  );

  const { data: templateCatalog, isError: isTemplateCatalogError } = useQuery(
    configurationsListMarketingPresentationTemplateCatalogOptions({
      client: centroPortalClient,
    }),
  );

  const { data: allBrokers = [] } = useQuery<BrokerPartner[]>({
    queryKey: ["prepareManageBrokers"],
    queryFn: () =>
      axiosInstance
        .get<BrokerPartner[]>("/brokerportal/v1/prepareManageBrokers")
        .then((resp) => resp.data),
  });

  useEffect(() => {
    if (config) {
      setName(config.name);
      setConfiguration(config.configuration ?? emptyConfiguration());
    }
  }, [config]);

  useEffect(() => {
    if (config && allBrokers.length > 0) {
      const assigned = config.assignments
        .map((a) => allBrokers.find((b) => b.id === a.brokerId))
        .filter((b): b is BrokerPartner => b !== undefined);
      setPendingAssignments(assigned);
    }
  }, [config, allBrokers]);

  const { mutate: updateConfig, isPending: isSaving } = useMutation({
    ...configurationsUpdateConfigurationMutation({
      client: centroPortalClient,
    }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: [{ tags: ["configurations"] }],
      });
      if (data.warnings?.length) {
        showError(
          `Saved, but some values were dropped: ${data.warnings
            .map((warning) => warning.field)
            .join(", ")}`,
        );
        return;
      }
      showSuccess("Configuration saved.");
    },
    onError: (err) => showError(extractErrorMessage(err)),
  });

  const { mutateAsync: syncAssignmentsAsync } = useMutation(
    configurationsSyncBrokerAssignmentsMutation({
      client: centroPortalClient,
    }),
  );

  function handleFieldToggle(
    sheetKey: string,
    fieldKey: string,
    value: boolean,
  ): void {
    setConfiguration((prev) => ({
      ...prev,
      quotes: {
        ...prev.quotes,
        [sheetKey]: {
          ...(prev.quotes[sheetKey] ?? {}),
          [fieldKey]: value,
        },
      },
    }));
  }

  function handleDollarDiffToggle(value: boolean): void {
    setConfiguration((prev) => ({
      ...prev,
      financial: { ...prev.financial, dollar_difference_from_current: value },
    }));
  }

  // The saved template stays in the options even while the catalog is loading, or
  // if the file has since left the flat template folder — otherwise MUI treats the
  // current value as invalid and renders the selection inconsistently.
  const catalogFileNames = (templateCatalog?.templates ?? []).map(
    (catalogEntry) => catalogEntry.fileName,
  );
  const templateOptions =
    configuration.template && !catalogFileNames.includes(configuration.template)
      ? [configuration.template, ...catalogFileNames]
      : catalogFileNames;

  function handleTemplateChange(template: string | null): void {
    setConfiguration((prev) => ({ ...prev, template: template ?? undefined }));
  }

  function handleGridColorChange(key: keyof GridStyle, value: string): void {
    setConfiguration((prev) => ({
      ...prev,
      presentation_style: {
        ...prev.presentation_style,
        grid: { ...prev.presentation_style?.grid, [key]: value },
      },
    }));
  }

  function handleThemeColorChange(key: keyof ThemeColors, value: string): void {
    setConfiguration((prev) => ({
      ...prev,
      presentation_style: {
        ...prev.presentation_style,
        theme: { ...prev.presentation_style?.theme, [key]: value },
      },
    }));
  }

  function handleSave(): void {
    if (!name.trim()) {
      setNameError("Name is required");
      return;
    }
    setNameError(null);
    updateConfig({
      path: { id: id! },
      body: {
        name: name.trim(),
        configuration: {
          ...configuration,
          presentation_style: prunePresentationStyle(
            configuration.presentation_style,
          ),
        },
      },
      client: centroPortalClient,
    });
  }

  async function handleSaveAssignments(): Promise<void> {
    setIsSavingAssignments(true);
    try {
      await syncAssignmentsAsync({
        path: { id: id! },
        body: { brokerIds: pendingAssignments.map((b) => b.id) },
        client: centroPortalClient,
      });
      void queryClient.invalidateQueries({
        queryKey: [{ tags: ["configurations"] }],
      });
      showSuccess("Broker assignments saved.");
    } catch (err) {
      showError(extractErrorMessage(err));
    } finally {
      setIsSavingAssignments(false);
    }
  }

  function handleCancelFieldSettings(): void {
    if (!config) return;
    setName(config.name);
    setConfiguration(config.configuration ?? emptyConfiguration());
    setNameError(null);
  }

  function handleCancelAssignments(): void {
    if (!config) return;
    const assigned = config.assignments
      .map((a) => allBrokers.find((b) => b.id === a.brokerId))
      .filter((b): b is BrokerPartner => b !== undefined);
    setPendingAssignments(assigned);
  }

  if (isLoading) return <Loader />;

  if (error) {
    return (
      <Box p={3} pt={12}>
        <Alert severity="error">
          {extractErrorMessage(error, "Failed to load configuration.")}
        </Alert>
      </Box>
    );
  }

  if (!config) return null;

  const unassignedBrokers = allBrokers.filter(
    (b) => !pendingAssignments.some((p) => p.id === b.id),
  );

  return (
    <Box p={3} pt={12} maxWidth={900} mx="auto">
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/${ROUTE_NAMES.MP_CONFIGS}`)}
          variant="tertiary"
        >
          MP Configs
        </Button>
        <Chip
          label={config.status}
          color={STATUS_COLORS[config.status] ?? "default"}
          size="small"
          sx={{ textTransform: "capitalize" }}
        />
        {config.isDefault && (
          <Chip label="Default" color="brandSupporting" size="small" />
        )}

        <Box display="flex" gap={2} ml="auto">
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            Save Changes
          </Button>
          <Button
            variant="secondary"
            disabled={isSaving}
            onClick={handleCancelFieldSettings}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      <Box display="flex" gap={3} alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="subtitle2" mb={1} color="textSecondary">
            Name
          </Typography>
          <TextField
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!nameError}
            helperText={nameError}
            size="small"
            sx={{ width: 320 }}
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" mb={1} color="textSecondary">
            Template
          </Typography>
          <Autocomplete
            options={templateOptions}
            value={configuration.template ?? null}
            onChange={(_, value) => handleTemplateChange(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Select a template"
                error={isTemplateCatalogError}
                helperText={
                  isTemplateCatalogError
                    ? "Failed to load the template catalog."
                    : "The workbook analysts export with for assigned brokers."
                }
              />
            )}
            sx={{ width: 380 }}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Tabs
        value={activeTab}
        onChange={(_, v: number) => setActiveTab(v)}
        sx={{ mb: 3 }}
      >
        <Tab label="Coverage Field Settings" />
        <Tab label="Presentation Styling" />
        <Tab label="Broker Assignments" />
      </Tabs>

      {activeTab === 0 && (
        <Box display="flex" flexDirection="column" gap={3}>
          {clientProfileSheet ? (
            <>
              <Box>
                <Typography variant="header-small" mb={1}>
                  Client Profile
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0}>
                  {clientProfileSheet.fields.map((field) => (
                    <FormControlLabel
                      key={field.key}
                      control={
                        <Switch
                          size="small"
                          checked={
                            configuration.quotes[clientProfileSheet.sheetKey]?.[
                              field.key
                            ] === true
                          }
                          onChange={(e) =>
                            handleFieldToggle(
                              clientProfileSheet.sheetKey,
                              field.key,
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label={field.label}
                      sx={{ width: "50%", mb: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>

              <Divider />
            </>
          ) : null}

          <Box>
            <Typography variant="header-small" mb={1}>
              Financial Summary
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={
                      configuration.financial.dollar_difference_from_current
                    }
                    onChange={(e) => handleDollarDiffToggle(e.target.checked)}
                  />
                }
                label="Dollar Savings to current"
                sx={{ width: "50%", mb: 0.5 }}
              />
              {/* The workbook's other Financial Summary controls live in the field
                catalog; they belong beside this toggle rather than under a second
                heading of the same name. */}
              {financialSummarySheet?.fields.map((field) => (
                <FormControlLabel
                  key={field.key}
                  control={
                    <Switch
                      size="small"
                      checked={
                        configuration.quotes[financialSummarySheet.sheetKey]?.[
                          field.key
                        ] === true
                      }
                      onChange={(e) =>
                        handleFieldToggle(
                          financialSummarySheet.sheetKey,
                          field.key,
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={field.label}
                  sx={{ width: "50%", mb: 0.5 }}
                />
              ))}
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="header-small" mb={2}>
              Coverage Field Settings
            </Typography>
            {/* One group per tab: 28 coverage sheets in a single column made
                finding one a scrolling exercise. */}
            <Tabs
              value={activeCoverageGroup}
              onChange={(_, group: string) => setActiveCoverageGroup(group)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2 }}
            >
              {coverageGroups.map((group) => (
                <Tab key={group} label={group} value={group} />
              ))}
            </Tabs>
            {coverageSheets
              .filter((sheet) => sheet.coverageGroup === activeCoverageGroup)
              .map((sheet) => (
                <Box key={sheet.sheetKey} mb={3}>
                  <Typography variant="subtitle1" color="textSecondary" mb={1}>
                    <strong>
                      {sheet.label}
                      {sheet.note ? (
                        <Tooltip title={sheet.note}>
                          {/* An IconButton so the note is reachable by keyboard:
                            the tooltip opens on focus as well as hover. */}
                          <IconButton
                            size="small"
                            aria-label={`About ${sheet.label} field settings`}
                            sx={{ ml: 0.5, p: 0.25 }}
                          >
                            <InfoOutlinedIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Chip
                        label={sheet.coverageGroup}
                        size="small"
                        sx={{ ml: 1 }}
                        variant="outlined"
                      />
                    </strong>
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0}>
                    {sheet.fields.map((field) => (
                      /* The note's icon is a sibling of the switch rather than
                         part of its label: inside it, a click on the icon would
                         toggle the setting the note explains. */
                      <Box
                        key={field.key}
                        display="flex"
                        alignItems="center"
                        sx={{ width: "50%", mb: 0.5 }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={
                                configuration.quotes[sheet.sheetKey]?.[
                                  field.key
                                ] === true
                              }
                              onChange={(e) =>
                                handleFieldToggle(
                                  sheet.sheetKey,
                                  field.key,
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={field.label}
                          sx={{ mr: 0 }}
                        />
                        {field.note ? (
                          <Tooltip title={field.note}>
                            <IconButton
                              size="small"
                              aria-label={`About ${field.label}`}
                              sx={{ p: 0.25 }}
                            >
                              <InfoOutlinedIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
          </Box>
        </Box>
      )}

      {activeTab === 1 && (
        <Box display="flex" flexDirection="column" gap={3}>
          <Box>
            <Typography variant="header-small" mb={1}>
              Workbook Theme
            </Typography>
            <Typography variant="body-medium" color="textSecondary" mb={2}>
              Overrides the workbook&apos;s Office theme colors, recoloring the
              template-driven coverage sheets. Leave a field empty to keep the
              template theme.
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              {THEME_COLOR_FIELDS.map((field) => (
                <ColorField
                  key={field.key}
                  label={field.label}
                  value={configuration.presentation_style?.theme?.[field.key]}
                  defaultHex={field.defaultHex}
                  onChange={(next) => handleThemeColorChange(field.key, next)}
                />
              ))}
            </Box>
          </Box>

          <Box>
            <Typography variant="header-small" mb={1}>
              Grid Colors
            </Typography>
            <Typography variant="body-medium" color="textSecondary" mb={2}>
              Colors for the generated data grids (Financial Summary, Comp Subs
              Credits, Carrier Response). Leave a field empty to use the
              default.
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              {GRID_STYLE_FIELDS.map((field) => (
                <ColorField
                  key={field.key}
                  label={field.label}
                  value={configuration.presentation_style?.grid?.[field.key]}
                  defaultHex={field.defaultHex}
                  onChange={(next) => handleGridColorChange(field.key, next)}
                />
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {activeTab === 2 && (
        <Box display="flex" flexDirection="column" gap={3}>
          <Autocomplete
            options={unassignedBrokers}
            getOptionLabel={(b) => b.name}
            renderInput={(params) => (
              <TextField {...params} label="Add broker" size="small" />
            )}
            inputValue={brokerInput}
            onInputChange={(_, value, reason) => {
              if (reason === "input") setBrokerInput(value);
            }}
            onChange={(_, broker) => {
              if (broker) {
                setPendingAssignments((prev) => [...prev, broker]);
                setBrokerInput("");
              }
            }}
            value={null}
            sx={{ width: 400 }}
          />

          <Box display="flex" flexDirection="column" gap={1}>
            {pendingAssignments.length === 0 && (
              <Typography color="textSecondary" variant="body-medium">
                No brokers assigned.
              </Typography>
            )}
            {pendingAssignments.map((broker) => (
              <Box key={broker.id} display="flex" alignItems="center" gap={2}>
                <Typography variant="body-medium">{broker.name}</Typography>
                {/* TODO(ds-button): needs manual review — outlined + color="error" has no DS variant */}
                <MuiButton
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() =>
                    setPendingAssignments((prev) =>
                      prev.filter((b) => b.id !== broker.id),
                    )
                  }
                >
                  Remove
                </MuiButton>
              </Box>
            ))}
          </Box>

          <Divider />

          <Box display="flex" gap={2}>
            <Button
              variant="primary"
              onClick={() => void handleSaveAssignments()}
              disabled={isSavingAssignments}
            >
              Save Assignments
            </Button>
            <Button
              variant="secondary"
              disabled={isSavingAssignments}
              onClick={handleCancelAssignments}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
